import { create } from 'zustand';
import { captureEvent } from '@/lib/analytics/posthog';

// Helper function to track events in both PostHog and Plausible
// Note: Plausible must be used within a React component with the usePlausible hook
// This function only handles PostHog tracking at the store level
const trackEvent = (
  eventName: string,
  properties?: Record<string, string | number | boolean | undefined | null>,
) => {
  // Track in PostHog
  captureEvent(eventName, properties);
};

interface ListState {
  // Search and filters
  searchTerm: string;
  category: string | undefined;
  sortBy: string;
  priceRange: { min: number | undefined; max: number | undefined };
  leadsRange: { min: number | undefined; max: number | undefined };
  containsEmails: boolean;
  containsVerifiedEmails: boolean;
  containsPhones: boolean;
  lastUpdatedDays: number | undefined;

  // Pagination
  currentPage: number;

  // Actions
  setSearchTerm: (term: string) => void;
  setCategory: (category: string | undefined) => void;
  setSortBy: (sort: string) => void;
  setPriceRange: (min?: number, max?: number) => void;
  setLeadsRange: (min?: number, max?: number) => void;
  setContainsEmails: (value: boolean) => void;
  setContainsVerifiedEmails: (value: boolean) => void;
  setContainsPhones: (value: boolean) => void;
  setLastUpdatedDays: (days?: number) => void;
  setCurrentPage: (page: number) => void;
  resetFilters: () => void;
}

export const useListStore = create<ListState>((set /*, get*/) => ({
  // Initial state
  searchTerm: '',
  category: undefined,
  sortBy: 'date_desc',
  priceRange: { min: undefined, max: undefined },
  leadsRange: { min: undefined, max: undefined },
  containsEmails: false,
  containsVerifiedEmails: false,
  containsPhones: false,
  lastUpdatedDays: undefined,
  currentPage: 1,

  // Actions
  setSearchTerm: (term) => {
    trackEvent('list_search', { term });
    set({ searchTerm: term, currentPage: 1 });
  },

  setCategory: (category) => {
    trackEvent('list_filter_category', { category: category || 'all' });
    set({ category, currentPage: 1 });
  },

  setSortBy: (sortBy) => {
    trackEvent('list_sort', { sortBy });
    set({ sortBy });
  },

  setPriceRange: (min, max) => {
    trackEvent('list_filter_price', { min, max });
    set({ priceRange: { min, max }, currentPage: 1 });
  },

  setLeadsRange: (min, max) => {
    trackEvent('list_filter_leads', { min, max });
    set({ leadsRange: { min, max }, currentPage: 1 });
  },

  setContainsEmails: (value) => {
    trackEvent('list_filter_emails', { containsEmails: value });
    set({ containsEmails: value, currentPage: 1 });
  },

  setContainsVerifiedEmails: (value) => {
    trackEvent('list_filter_verified_emails', { containsVerifiedEmails: value });
    set({ containsVerifiedEmails: value, currentPage: 1 });
  },

  setContainsPhones: (value) => {
    trackEvent('list_filter_phones', { containsPhones: value });
    set({ containsPhones: value, currentPage: 1 });
  },

  setLastUpdatedDays: (days) => {
    trackEvent('list_filter_last_updated', { days });
    set({ lastUpdatedDays: days, currentPage: 1 });
  },

  setCurrentPage: (page) => {
    trackEvent('list_pagination', { page });
    set({ currentPage: page });
  },

  resetFilters: () => {
    trackEvent('list_reset_filters');
    set({
      searchTerm: '',
      category: undefined,
      priceRange: { min: undefined, max: undefined },
      leadsRange: { min: undefined, max: undefined },
      containsEmails: false,
      containsVerifiedEmails: false,
      containsPhones: false,
      lastUpdatedDays: undefined,
      currentPage: 1,
    });
  },
}));
